import React, {useEffect, useState}                                   from 'react'
import {
	Box,
	CircularProgress,
	Dialog,
	DialogContent,
	IconButton,
	Typography,
	useTheme
}                                                                    from '@mui/material'
import {AutoAwesome, Check, Close, KeyboardArrowDown}                from '@mui/icons-material'
import {
	currentTutorStyle,
	fetchTutorStyles,
	saveTutorStyle,
	TUTOR_STYLE_LABEL
}                                                                    from '../../util/tutor-style'
import {fetchUserProfile}                                            from '../../util/mini'
import {useUserStore}                                                from '../../state'

/*
 * The Tutor Style badge and its picker (142).
 *
 * Sits beside Continue Studying on the dashboard. The badge names the style the student is
 * currently taught in; clicking it opens the list to change.
 *
 * The styles are fetched when the badge mounts rather than when the dialog opens, because the badge
 * has to NAME the current one — it cannot label itself from an id alone. One small request per
 * mount, and it is what lets the dialog open with the list already in hand.
 *
 * SELF-SUFFICIENT WHEN UNPARENTED (170). It moved to the navbar, which sits above every page and has
 * no prepkit or profile of its own, so with no `userProfile` prop the picker reads the profile
 * itself. A passed-in profile still wins — that keeps a parent which already holds the row from
 * causing a second read of it.
 *
 * `compact` is the navbar form: one line, no label above it. Label-over-value is right in a page
 * header but does not fit a 56px bar.
 */

/*
 * One style, as a selectable card. The description is the personality's own copy from the
 * `ai_personality` table, so what a student reads here is what an admin wrote on that screen.
 */
const StyleCard = ({style, custom, selected, saving, onSelect}) => (
	<Box
		component="button"
		type="button"
		data-ga="tutor-style-option"
		disabled={saving}
		onClick={() => onSelect(style.id)}
		sx={{
			font: 'inherit',
			textAlign: 'left',
			width: '100%',
			display: 'flex',
			alignItems: 'flex-start',
			gap: 1.5,
			p: 2,
			mb: 1.5,
			borderRadius: `${custom.radius.md}px`,
			cursor: saving ? 'default' : 'pointer',
			background: selected ? custom.bg.raised : custom.bg.surface,
			// selection is carried by the outline, the same way the filter chips do it
			border: `2px solid ${selected ? custom.indigo.primary : custom.border.color}`,
			transition: 'border-color 150ms, background 150ms',
			'&:hover': saving ? undefined : {
				borderColor: selected ? custom.indigo.primary : custom.border.strong,
				background: custom.bg.raised,
			},
		}}
	>
		<Box sx={{
			flexShrink: 0,
			width: 22,
			height: 22,
			mt: '2px',
			borderRadius: '50%',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			background: selected ? custom.indigo.primary : 'transparent',
			border: `2px solid ${selected ? custom.indigo.primary : custom.border.strong}`,
			color: '#fff',
		}}>
			{selected && <Check sx={{fontSize: 14}}/>}
		</Box>

		<Box sx={{minWidth: 0}}>
			<Typography sx={{
				fontSize: custom.fontSize.md,
				fontWeight: custom.fontWeight.bold,
				color: custom.text.main,
				lineHeight: 1.35,
			}}>
				{style.name}
			</Typography>
			<Typography sx={{mt: 0.5, fontSize: custom.fontSize.sm, color: custom.text.muted, lineHeight: 1.5}}>
				{style.description}
			</Typography>
		</Box>
	</Box>
)

const TutorStylePicker = ({userProfile: userProfileProp, custom: customProp, onChange, compact = false}) => {
	const theme = useTheme()
	const custom = customProp || theme.custom
	const {user} = useUserStore()

	const [styles, setStyles] = useState(null)
	const [ownProfile, setOwnProfile] = useState(null)
	const [open, setOpen] = useState(false)
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState(null)

	/*
	 * Signed-in users only. The navbar also renders over public pages — /prep, an anonymous question
	 * — where both of these calls would 401, so they are not made rather than made and swallowed.
	 */
	useEffect(() => {
		if (!user) return
		let cancelled = false
		fetchTutorStyles()
			.then(data => { if (!cancelled) setStyles(data || []) })
			.catch(() => { if (!cancelled) setStyles([]) })
		return () => { cancelled = true }
	}, [user])

	useEffect(() => {
		if (userProfileProp || !user || !user.id) return
		let cancelled = false
		fetchUserProfile(user.id)
			.then(p => { if (!cancelled) setOwnProfile(p) })
			.catch(() => {})
		return () => { cancelled = true }
	}, [userProfileProp, user])

	const userProfile = userProfileProp || ownProfile

	const active = currentTutorStyle(styles, userProfile && userProfile.ai_personality_id)

	/*
	 * Nothing to show until the styles are known. Deliberately renders nothing rather than a
	 * skeleton: the badge sits next to the primary action on the page, and a placeholder that
	 * becomes a button would shift Continue Studying sideways as it resolves.
	 */
	if (!active) return null

	const choose = async (id) => {
		if (id === active.id) {
			setOpen(false)
			return
		}

		setSaving(true)
		setError(null)

		try {
			const saved = await saveTutorStyle(id)

			/*
			 * Hand the saved row up so the dashboard's profile matches the database — without it the
			 * badge keeps naming the old style until a reload while the AI already answers in the
			 * new one.
			 *
			 * MERGED over the existing profile rather than replacing it. The row comes back complete
			 * today, but anything missing from it would be read as absent by whatever else reads the
			 * profile — and on this screen a missing `welcomed_at` is the condition for showing the
			 * first-run Welcome flow. Merging makes the update additive, so a save can only ever
			 * change the field it was asked to change.
			 */
			const merged = {...userProfile, ...(saved || {}), ai_personality_id: id}
			if (onChange) onChange(merged)
			// unparented, there is no onChange to tell, so it updates the profile it owns
			if (!userProfileProp) setOwnProfile(merged)
			setOpen(false)
		}
		catch (err) {
			setError(err.message || 'Could not save your tutor style.')
		}
		finally {
			setSaving(false)
		}
	}

	return (
		<>
			{/*
			  * Transparent and borderless (143). It sits beside Continue Studying, and as a bordered
			  * chip it read as a second button competing with the primary one. A setting the student
			  * changes rarely should recede until looked at — the hover fill is what says it is a
			  * control, and the caret says so at rest.
			  *
			  * Two lines: the label over the value. On one line the label had to be dropped below sm
			  * to fit, which left the name alone reading as a status rather than something clickable.
			  * Stacked, both survive at every width.
			  */}
			<Box
				component="button"
				type="button"
				data-ga="tutor-style"
				aria-label={`${TUTOR_STYLE_LABEL}: ${active.name}. Change it.`}
				onClick={() => setOpen(true)}
				sx={{
					font: 'inherit',
					display: 'flex',
					...(compact
						? {alignItems: 'center', gap: 0.5}
						: {flexDirection: 'column', alignItems: 'flex-start', gap: 0.125}),
					px: 1,
					py: compact ? 0.5 : 0.75,
					borderRadius: `${custom.radius.sm}px`,
					cursor: 'pointer',
					whiteSpace: 'nowrap',
					background: 'transparent',
					border: 'none',
					color: custom.text.main,
					transition: 'background 150ms',
					'&:hover': {background: custom.bg.dropdownHover},
				}}
			>
				{compact
					? (
						<>
							<AutoAwesome sx={{fontSize: 16, color: custom.status.amber}}/>
							{/*
							  * The name drops below sm. Beside the hamburger there is no room for
							  * "Fun and Friendly" on a phone, and the icon plus caret still reads as a
							  * control — the dialog names the setting once it is open.
							  */}
							<Box component="span" sx={{
								display: {xs: 'none', sm: 'inline'},
								fontSize: custom.fontSize.sm,
								fontWeight: custom.fontWeight.semibold,
							}}>
								{active.name}
							</Box>
							<KeyboardArrowDown sx={{fontSize: 16, color: custom.text.muted}}/>
						</>
					)
					: (
						<>
							<Box component="span" sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
								<AutoAwesome sx={{fontSize: 14, color: custom.status.amber}}/>
								<Box component="span" sx={{
									fontSize: custom.fontSize.xxs,
									fontWeight: custom.fontWeight.semibold,
									textTransform: 'uppercase',
									letterSpacing: '0.06em',
									color: custom.text.muted,
								}}>
									{TUTOR_STYLE_LABEL}
								</Box>
							</Box>

							<Box component="span" sx={{display: 'flex', alignItems: 'center', gap: 0.25}}>
								<Box component="span" sx={{
									fontSize: custom.fontSize.sm,
									fontWeight: custom.fontWeight.semibold,
								}}>
									{active.name}
								</Box>
								<KeyboardArrowDown sx={{fontSize: 16, color: custom.text.muted}}/>
							</Box>
						</>
					)
				}
			</Box>


			<Dialog
				open={open}
				onClose={() => !saving && setOpen(false)}
				fullWidth
				maxWidth="sm"
				PaperProps={{sx: {
					background: custom.bg.surface,
					borderRadius: `${custom.radius.lg}px`,
					backgroundImage: 'none',
				}}}
			>
				<DialogContent sx={{p: 3}}>
					<Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 0.5}}>
						<Typography sx={{
							fontSize: {xs: 20, md: 22},
							fontWeight: custom.fontWeight.bold,
							color: custom.text.main,
						}}>
							Choose your {TUTOR_STYLE_LABEL}
						</Typography>
						<IconButton size="small" onClick={() => setOpen(false)} disabled={saving} aria-label="Close">
							<Close fontSize="small"/>
						</IconButton>
					</Box>

					<Typography sx={{mb: 2.5, fontSize: custom.fontSize.sm, color: custom.text.muted, lineHeight: 1.5}}>
						This is how Birdsy talks to you — in the chat beside each question, and in your
						topic summaries. You can change it whenever you like.
					</Typography>

					{styles.map(style => (
						<StyleCard
							key={style.id}
							style={style}
							custom={custom}
							selected={style.id === active.id}
							saving={saving}
							onSelect={choose}
						/>
					))}

					{saving && (
						<Box sx={{display: 'flex', alignItems: 'center', gap: 1, mt: 1}}>
							<CircularProgress size={14} sx={{color: custom.text.muted}}/>
							<Typography sx={{fontSize: custom.fontSize.sm, color: custom.text.muted}}>
								Saving…
							</Typography>
						</Box>
					)}

					{error && (
						<Typography sx={{mt: 1, fontSize: custom.fontSize.sm, color: custom.status.danger}}>
							{error}
						</Typography>
					)}
				</DialogContent>
			</Dialog>
		</>
	)
}

export default TutorStylePicker
